import { cloneElement, isValidElement, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Add, ArrowLeft2, ArrowRight2, Trash } from "iconsax-react";
import moment from "moment-jalaali";
import DBModal from "../../../component/global/DBModal";
import DBButton from "../../../component/global/DBButton";
import DatePickerModal from "../../../component/global/DatePicker";
import SearchSelectBox from "../../../component/global/SearchSelectBox";
import { ToastContext } from "../../../component/global/ToastContext";
import { get, post, put } from "../../../controlers/global/ConfigRequest";
import {
    ADD_DISCOUNT_CARD,
    COMPANY_LIST,
    EDIT_DISCOUNT_CARD,
    GET_ONE_DISCOUNT_CARD,
} from "../../../helperData/ApiAddress";
import TargetPicker from "./TargetPicker";
import { inferTarget, resolveSaleCartIds } from "./helpers";

const STEPS = ["target", "terms", "rules"];
const emptyDate = { fa: "", en: "", set: false };

function Field({ id, label, error, hint, children }) {
    const errorId = error ? `${id}-error` : undefined;
    const hintId = hint ? `${id}-hint` : undefined;
    const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;
    const control = isValidElement(children)
        ? cloneElement(children, {
              id: children.props.id || id,
              "aria-invalid": error ? true : undefined,
              "aria-describedby": describedBy,
          })
        : children;
    return (
        <div className={`offer-field${error ? " has-error" : ""}`}>
            <label htmlFor={id}>{label}</label>
            {control}
            {hint && (
                <p id={hintId} className="offer-field__hint">
                    {hint}
                </p>
            )}
            {error && (
                <p id={errorId} className="offer-field__error" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
}

function toBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

export default function OfferEditor({
    show,
    onHide,
    editId,
    carts,
    onSaved,
}) {
    const { t, i18n } = useTranslation();
    const showToast = useContext(ToastContext);
    const formId = useId().replace(/:/g, "");
    const isRtl = i18n.dir() === "rtl";
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [cardType, setCardType] = useState("1");
    const [cardNum, setCardNum] = useState("");
    const [sale, setSale] = useState("");
    const [logo, setLogo] = useState("");
    const [fileName, setFileName] = useState("");
    const [startDate, setStartDate] = useState(emptyDate);
    const [endDate, setEndDate] = useState(emptyDate);
    const [startOpen, setStartOpen] = useState(false);
    const [endOpen, setEndOpen] = useState(false);
    const [kind, setKind] = useState("itemType");
    const [selectedId, setSelectedId] = useState(null);
    const [companyList, setCompanyList] = useState({ children: [] });
    const [orgCode, setOrgCode] = useState(null);
    const [userType, setUserType] = useState("1");
    const [score, setScore] = useState("0");
    const [rules, setRules] = useState([]);
    const [errors, setErrors] = useState({});

    const cartsRef = useRef(carts);
    cartsRef.current = carts;
    const saleCartIds = useMemo(
        () => resolveSaleCartIds(kind, selectedId, carts),
        [kind, selectedId, carts]
    );

    useEffect(() => {
        if (!show) {
            return undefined;
        }
        setStep(0);
        setErrors({});
        get(COMPANY_LIST).then((response) => {
            setCompanyList({
                id: 0,
                name: "",
                toggled: true,
                children: [{ id: null, name: t("offers.editor.allOrgs"), children: [] }].concat(
                    (response || []).map((item) => ({ ...item, children: [] }))
                ),
            });
        });
        if (!editId) {
            setTitle("");
            setDescription("");
            setCardType("1");
            setCardNum("");
            setSale("");
            setLogo("");
            setFileName("");
            setStartDate(emptyDate);
            setEndDate(emptyDate);
            setKind("itemType");
            setSelectedId(null);
            setRules([]);
            setOrgCode(null);
            setUserType("1");
            setScore("0");
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        get(GET_ONE_DISCOUNT_CARD.replace("{id}/", `${editId}/`))
            .then((card) => {
                setTitle(card?.name || "");
                setDescription(card?.description || "");
                setCardNum(
                    card?.discount_cards_count != null
                        ? String(card.discount_cards_count)
                        : ""
                );
                setSale(
                    card?.discount_amount != null ? String(card.discount_amount) : ""
                );
                setCardType(String(card?.discount_type || "1"));
                setStartDate({
                    fa: card?.date_start
                        ? moment(card.date_start).format("jYYYY-jMM-jDD")
                        : "",
                    en: card?.date_start || "",
                    set: Boolean(card?.date_start),
                });
                setEndDate({
                    fa: card?.date_end
                        ? moment(card.date_end).format("jYYYY-jMM-jDD")
                        : "",
                    en: card?.date_end || "",
                    set: Boolean(card?.date_end),
                });
                const inferred = inferTarget(card?.sale_cart, cartsRef.current);
                setKind(inferred.kind);
                setSelectedId(inferred.id);
                setRules(
                    (card?.requireds || []).map((item) => ({
                        id: item.id,
                        club_level: String(item.club_level),
                        required_score: item.required_score,
                        target_company: item.target_company?.id ?? null,
                    }))
                );
            })
            .finally(() => setLoading(false));
        return undefined;
    }, [show, editId, t]);

    const companyName = (id) => {
        if (id == null) {
            return t("offers.editor.allOrgs");
        }
        return (
            companyList.children?.find((item) => item.id === id)?.name ||
            t("offers.editor.allOrgs")
        );
    };

    const addRule = () => {
        if (rules.some((item) => item.target_company === orgCode && item.club_level === userType)) {
            setErrors((current) => ({
                ...current,
                org: t("offers.editor.duplicateRule"),
            }));
            return;
        }
        if (score === "" || Number(score) < 0) {
            setErrors((current) => ({
                ...current,
                score: t("offers.editor.needScore"),
            }));
            return;
        }
        setRules((current) => [
            ...current,
            {
                id: Date.now(),
                club_level: userType,
                required_score: Number(score),
                target_company: orgCode,
            },
        ]);
        setErrors((current) => ({ ...current, org: "", score: "", rules: "" }));
    };

    const validateStep = (index) => {
        const next = {};
        if (index === 0 && saleCartIds.length === 0) {
            next.target = t("offers.editor.needTarget");
        }
        if (index === 1) {
            if (!title.trim()) next.title = t("offers.editor.needTitle");
            if (!description.trim()) next.description = t("offers.editor.needDescription");
            if (cardNum === "" || Number(cardNum) < 0) {
                next.cardNum = t("offers.editor.needCount");
            }
            if (sale === "" || Number(sale) < 0) {
                next.sale = t("offers.editor.needAmount");
            }
        }
        if (index === 2 && rules.length === 0) {
            next.rules = t("offers.editor.needRules");
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const goNext = () => {
        if (validateStep(step)) {
            setStep((current) => Math.min(current + 1, STEPS.length - 1));
        }
    };

    const save = () => {
        if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
            if (saleCartIds.length === 0) setStep(0);
            else if (rules.length === 0) setStep(2);
            else setStep(1);
            return;
        }
        setSaving(true);
        const payload = {
            name: title.trim(),
            description: description.trim(),
            discount_amount: Number(sale),
            discount_type: Number(cardType),
            discount_cards_count: Number(cardNum),
            sale_cart: saleCartIds,
            date_start: startDate.en || null,
            date_end: endDate.en || null,
            requireds: rules.map((item) => ({
                club_level: Number(item.club_level),
                required_score: Number(item.required_score),
                target_company: item.target_company,
            })),
        };
        if (logo) {
            payload.logo = logo;
        }
        const request = editId
            ? put(EDIT_DISCOUNT_CARD.replace("{id}", editId), payload)
            : post(ADD_DISCOUNT_CARD, payload);
        request
            .then(() => {
                showToast(t("offers.editor.success"), "success");
                onSaved();
                onHide();
            })
            .catch(() => showToast(t("offers.editor.error"), "danger"))
            .finally(() => setSaving(false));
    };

    const onFile = async (event) => {
        const file = event.currentTarget.files?.[0];
        if (!file) {
            return;
        }
        if (!file.type?.startsWith("image/")) {
            setErrors((current) => ({ ...current, logo: t("offers.editor.badFile") }));
            setFileName("");
            setLogo("");
            return;
        }
        if (file.size > 1024 * 1024) {
            setErrors((current) => ({ ...current, logo: t("offers.editor.bigFile") }));
            setFileName("");
            setLogo("");
            return;
        }
        setErrors((current) => ({ ...current, logo: "" }));
        setFileName(file.name);
        setLogo(await toBase64(file));
    };

    const levelLabel = (level) => {
        if (Number(level) === 2) return t("offers.level.active");
        if (Number(level) === 3) return t("offers.level.supporter");
        return t("offers.level.normal");
    };

    return (
        <DBModal
            show={show}
            onHide={onHide}
            minWidth={760}
            className="offer-editor-modal"
        >
            <DBModal.Header
                title={editId ? t("offers.editor.editTitle") : t("offers.editor.createTitle")}
                closeBtn={onHide}
                closeLabel={t("offers.editor.cancel")}
            />
            <DBModal.Content>
                {loading ? (
                    <div className="offer-state" role="status" aria-live="polite">
                        <Spinner animation="border" size="sm" />
                        {t("offers.loading")}
                    </div>
                ) : (
                    <form
                        className="offer-editor"
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (step === STEPS.length - 1) save();
                            else goNext();
                        }}
                    >
                        <ol className="offer-steps" aria-label={t("offers.editor.steps")}>
                            {STEPS.map((key, index) => (
                                <li
                                    key={key}
                                    aria-current={step === index ? "step" : undefined}
                                    className={step === index ? "is-current" : ""}
                                >
                                    <span>{index + 1}</span>
                                    {t(`offers.editor.step.${key}`)}
                                </li>
                            ))}
                        </ol>
                        {step === 0 && (
                            <section>
                                {errors.target && (
                                    <p className="offer-field__error" role="alert">
                                        {errors.target}
                                    </p>
                                )}
                                <TargetPicker
                                    carts={carts}
                                    kind={kind}
                                    selectedId={selectedId}
                                    onChange={({ kind: nextKind, selectedId: nextId }) => {
                                        setKind(nextKind);
                                        setSelectedId(nextId);
                                    }}
                                />
                            </section>
                        )}
                        {step === 1 && (
                            <section className="offer-editor__grid">
                                <Field
                                    id={`${formId}-title`}
                                    label={t("offers.editor.title")}
                                    error={errors.title}
                                >
                                    <input
                                        id={`${formId}-title`}
                                        value={title}
                                        onChange={(event) => setTitle(event.target.value)}
                                    />
                                </Field>
                                <Field
                                    id={`${formId}-description`}
                                    label={t("offers.editor.description")}
                                    error={errors.description}
                                >
                                    <textarea
                                        id={`${formId}-description`}
                                        value={description}
                                        rows={3}
                                        onChange={(event) => setDescription(event.target.value)}
                                    />
                                </Field>
                                <Field
                                    id={`${formId}-type`}
                                    label={t("offers.editor.discountType")}
                                >
                                    <select
                                        id={`${formId}-type`}
                                        value={cardType}
                                        onChange={(event) => setCardType(event.target.value)}
                                    >
                                        <option value="1">{t("offers.editor.percent")}</option>
                                        <option value="2">{t("offers.editor.cash")}</option>
                                    </select>
                                </Field>
                                <Field
                                    id={`${formId}-amount`}
                                    label={t("offers.editor.amount")}
                                    error={errors.sale}
                                >
                                    <input
                                        id={`${formId}-amount`}
                                        type="number"
                                        min="0"
                                        inputMode="numeric"
                                        value={sale}
                                        onChange={(event) => setSale(event.target.value)}
                                    />
                                </Field>
                                <Field
                                    id={`${formId}-count`}
                                    label={t("offers.editor.count")}
                                    error={errors.cardNum}
                                >
                                    <input
                                        id={`${formId}-count`}
                                        type="number"
                                        min="0"
                                        inputMode="numeric"
                                        value={cardNum}
                                        onChange={(event) => setCardNum(event.target.value)}
                                    />
                                </Field>
                                <Field
                                    id={`${formId}-start`}
                                    label={t("offers.editor.from")}
                                >
                                    <input
                                        id={`${formId}-start`}
                                        readOnly
                                        value={startDate.fa}
                                        onClick={() => setStartOpen(true)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                setStartOpen(true);
                                            }
                                        }}
                                    />
                                    <DatePickerModal
                                        show={startOpen}
                                        close={setStartOpen}
                                        defaultValue={startDate.fa}
                                        onChange={setStartDate}
                                    />
                                </Field>
                                <Field id={`${formId}-end`} label={t("offers.editor.until")}>
                                    <input
                                        id={`${formId}-end`}
                                        readOnly
                                        value={endDate.fa}
                                        onClick={() => setEndOpen(true)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                setEndOpen(true);
                                            }
                                        }}
                                    />
                                    <DatePickerModal
                                        show={endOpen}
                                        close={setEndOpen}
                                        defaultValue={endDate.fa}
                                        onChange={setEndDate}
                                    />
                                </Field>
                                <Field
                                    id={`${formId}-logo`}
                                    label={t("offers.editor.logo")}
                                    error={errors.logo}
                                    hint={fileName}
                                >
                                    <input
                                        id={`${formId}-logo`}
                                        type="file"
                                        accept="image/*"
                                        onChange={onFile}
                                    />
                                </Field>
                            </section>
                        )}
                        {step === 2 && (
                            <section>
                                <p className="offer-target__hint">
                                    {t("offers.editor.rulesHint")}
                                </p>
                                <div className="offer-rule-form">
                                    <Field
                                        id={`${formId}-user-type`}
                                        label={t("offers.editor.userType")}
                                    >
                                        <select
                                            id={`${formId}-user-type`}
                                            value={userType}
                                            onChange={(event) => setUserType(event.target.value)}
                                        >
                                            <option value="1">{t("offers.level.normal")}</option>
                                        </select>
                                    </Field>
                                    <Field
                                        id={`${formId}-org`}
                                        label={t("offers.editor.org")}
                                        error={errors.org}
                                    >
                                        {companyList.children?.length > 0 && (
                                            <SearchSelectBox
                                                options={companyList}
                                                defaultValue=""
                                                keyForSelect="parent"
                                                onSelectFunction={(_key, _equal, item) =>
                                                    setOrgCode(item.id)
                                                }
                                                specialClass="form_filed"
                                                readOnly={false}
                                            />
                                        )}
                                    </Field>
                                    <Field
                                        id={`${formId}-score`}
                                        label={t("offers.editor.score")}
                                        error={errors.score}
                                    >
                                        <input
                                            id={`${formId}-score`}
                                            type="number"
                                            min="0"
                                            value={score}
                                            onChange={(event) => setScore(event.target.value)}
                                        />
                                    </Field>
                                    <button
                                        type="button"
                                        className="offer-icon-btn"
                                        onClick={addRule}
                                        aria-label={t("offers.editor.addRule")}
                                    >
                                        <Add size={18} aria-hidden="true" />
                                    </button>
                                </div>
                                {errors.rules && (
                                    <p className="offer-field__error" role="alert">
                                        {errors.rules}
                                    </p>
                                )}
                                <table className="offer-rules">
                                    <caption className="visually-hidden">
                                        {t("offers.editor.rulesCaption")}
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th>{t("offers.editor.userType")}</th>
                                            <th>{t("offers.editor.org")}</th>
                                            <th>{t("offers.editor.score")}</th>
                                            <th>{t("offers.delete")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rules.map((item) => (
                                            <tr key={item.id}>
                                                <td>{levelLabel(item.club_level)}</td>
                                                <td>{companyName(item.target_company)}</td>
                                                <td className="is-tabular">
                                                    {item.required_score}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="offer-icon-btn is-danger"
                                                        aria-label={t("offers.delete")}
                                                        onClick={() =>
                                                            setRules((current) =>
                                                                current.filter(
                                                                    (rule) => rule.id !== item.id
                                                                )
                                                            )
                                                        }
                                                    >
                                                        <Trash size={16} aria-hidden="true" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        )}
                    </form>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <div className="offer-editor__footer">
                    <DBButton
                        type="secondary"
                        caption={t("offers.editor.cancel")}
                        onClick={onHide}
                    />
                    {step > 0 && (
                        <DBButton
                            type="secondary"
                            outlined
                            caption={
                                <>
                                    {isRtl ? (
                                        <ArrowRight2 size={16} aria-hidden="true" />
                                    ) : (
                                        <ArrowLeft2 size={16} aria-hidden="true" />
                                    )}
                                    {t("offers.editor.back")}
                                </>
                            }
                            onClick={() => setStep((current) => current - 1)}
                        />
                    )}
                    <DBButton
                        type="primary"
                        loading={saving}
                        caption={
                            step === STEPS.length - 1
                                ? t("offers.editor.save")
                                : t("offers.editor.next")
                        }
                        onClick={step === STEPS.length - 1 ? save : goNext}
                    />
                </div>
            </DBModal.Footer>
        </DBModal>
    );
}
